import { Link, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { blogPosts } from '../data/blogData';
import bgImage from '../Assets/TeamandOthers/Blog-bb7.webp';
import doctorImage from "../Assets/TeamandOthers/DR1.png";
import { Helmet } from "react-helmet";
// Helper: Convert a title to a URL slug
const slugify = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

const categories = [
  "All", "Health", "Tips", "Implants", "Technology", "Pediatric", "Cosmetic", "Orthodontics"
];

const POSTS_PER_PAGE = 4;

const Blog = () => {
  const location = useLocation();
  const mainContentRef = useRef(null);

  // Initialize state from location.state if returning from a detail page
  const [activeCategory, setActiveCategory] = useState(location.state?.returnCat || "All");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(location.state?.returnPage || 1);

  // Effect to handle scrolling back to the specific post position
  useEffect(() => {
    if (location.state?.returnPostId) {
      const targetId = `post-${location.state.returnPostId}`;
      const timer = setTimeout(() => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // 1. Category filter
  let filtered = activeCategory === "All"
    ? blogPosts
    : blogPosts.filter(post => post.category === activeCategory);

  // 2. Search filter (on title + excerpt)
  if (searchTerm.trim()) {
    const term = searchTerm.toLowerCase().trim();
    filtered = filtered.filter(post =>
      post.title.toLowerCase().includes(term) ||
      post.excerpt.toLowerCase().includes(term)
    );
  }

  // 3. Pagination
  const totalPosts = filtered.length;
  const totalPages = Math.ceil(totalPosts / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const paginatedPosts = filtered.slice(startIndex, startIndex + POSTS_PER_PAGE);

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setCurrentPage(1);
    scrollToMainContent(); 
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const scrollToMainContent = () => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
  <title>Dental Tips & Oral Care Blog | Dr. Sridhar Dental</title>
  <meta name="description" content="Read expert dental tips, oral care advice and latest treatments from Dr. Sridhar Dental Clinic Hyderabad." />
  <meta name="keywords" content="dental blog Hyderabad, oral care tips, dental advice" />
  <meta name="author" content="Dr Sridhar Dental" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://sridhardental.com/blog" />
</Helmet>
      {/* Hero / Page Title */}
      <section
        className="relative text-white md:py-0 overflow-hidden"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="relative container mx-auto px-4 flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 text-center md:text-left z-10">
            <p className="text-lg uppercase tracking-wide text-blue-800 mb-2 font-bold">
              Blogs from
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Dr Arumalla Sridhar Reddy
            </h1>
            <p className="text-xl md:text-2xl opacity-90 mb-3">
              BDS, MDS – Oral & Maxillofacial Surgeon
            </p>
            <p className="text-lg text-blue-800 font-bold">
              Founder & Chairman
            </p>
          </div>
          <div className="mt-10 md:mt-0 justify-center md:justify-end ml-[15%] sm:mt-[10%]">
            <img
              src={doctorImage}
              alt="Dr Arumalla Sridhar Reddy"
              className="max-h-[350px] md:max-h-[420px] object-contain drop-shadow-2xl md:ml-[8%]"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-28" ref={mainContentRef}>
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Blog Posts Grid */}
            <div className="lg:w-3/4">
              {/* Category Filter */}
              <div className="flex flex-wrap gap-3 mb-6">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => handleCategoryChange(cat)}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition ${
                      activeCategory === cat
                        ? "bg-blue-600 text-white shadow-md"
                        : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="mb-10">
                <div className="relative max-w-md">
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Posts Grid */}
              {paginatedPosts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {paginatedPosts.map((post) => (
                    <article
                      key={post.id}
                      id={`post-${post.id}`} // Used for scroll positioning
                      className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-[480px] md:h-[520px]"
                    >
                      <div className="relative h-52 overflow-hidden shrink-0">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                        />
                        <span className="absolute top-4 left-4 bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                      </div>

                      <div className="p-6 flex flex-col flex-grow">
                        <div className="flex items-center text-sm text-gray-500 mb-3">
                          <span>{post.date}</span>
                          <span className="mx-2">•</span>
                          <span>{post.readTime}</span>
                        </div>

                        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 line-clamp-2">
                          {post.title}
                        </h2>

                        <p className="text-gray-600 mb-6 line-clamp-4 flex-grow">
                          {post.excerpt}
                        </p>

                        <Link
                          to={`/blog/${slugify(post.title)}`}
                          state={{ fromPage: currentPage, fromCat: activeCategory }}
                          className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-800 mt-auto group"
                        >
                          <span className="ml-2 transform group-hover:translate-x-1 transition-transform">Read More →</span>
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 text-gray-500 text-xl">
                  No posts found matching your criteria.
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-12 gap-3 flex-wrap">
                  <button
                    onClick={() => {
                      setCurrentPage(prev => Math.max(prev - 1, 1));
                      scrollToMainContent();
                    }}
                    disabled={currentPage === 1}
                    className="px-5 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => {
                        setCurrentPage(page);
                        scrollToMainContent();
                      }}
                      className={`px-5 py-3 rounded-lg font-medium ${
                        currentPage === page
                          ? "bg-blue-600 text-white"
                          : "bg-white border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    onClick={() => {
                      setCurrentPage(prev => Math.min(prev + 1, totalPages));
                      scrollToMainContent();
                    }}
                    disabled={currentPage === totalPages}
                    className="px-5 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <aside className="lg:w-1/4 space-y-10 lg:mt-[12%]">
              <div className="bg-white p-6 rounded-2xl shadow-md">
                <h3 className="text-xl font-bold mb-6">Recent Posts</h3>
                <div className="space-y-6">
                  {blogPosts.slice(0, 4).map((post) => (
                    <div key={post.id} className="flex gap-4">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div>
                        <Link
                          to={`/blog/${slugify(post.title)}`}
                          state={{ fromPage: currentPage, fromCat: activeCategory }}
                          className="font-medium text-gray-900 hover:text-blue-600 line-clamp-2"
                        >
                          {post.title}
                        </Link>
                        <p className="text-sm text-gray-500 mt-1">{post.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-md">
                <h3 className="text-xl font-bold mb-6">Categories</h3>
                <ul className="space-y-3">
                  {categories.slice(1).map((cat) => (
                    <li key={cat}>
                      <button
                        onClick={() => handleCategoryChange(cat)}
                        className={`text-gray-700 hover:text-blue-600 transition flex items-center ${
                          activeCategory === cat ? "font-semibold text-blue-600" : ""
                        }`}
                      >
                        <span className="w-2 h-2 bg-blue-600 rounded-full mr-3"></span>
                        {cat}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Blog;